import { create } from "zustand"
import { persist } from "zustand/middleware"
import type {
  User,
  UserRole,
  Measurement,
  Alert,
  AlarmLimits,
  SensorInfo,
  SiteBlock,
} from "./types"

/* =========================
   AUTH STORE (LOGIN REAL)
========================= */

export const DEMO_MODE = true

const DEMO_USER: User = {
  id: "demo",
  email: "demo@totalcontrolsystem.com",
  name: "Demo Viewer",
  //role: "admin",
  role: "viewer",
  createdAt: new Date(),
  isPro: false,
  isBasic: false,
  isPremium: true,
}

interface AuthStore {
  user: User | null
  isAuthenticated: boolean
  isDemo: boolean
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  checkRole: (allowedRoles: UserRole[]) => boolean
  refreshUserStatus: () => Promise<void>
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isDemo: false,

      login: async (email: string, password: string) => {
        if (DEMO_MODE) {
          set({
            user: { ...DEMO_USER },
            isAuthenticated: true,
            isDemo: true,
          })
          return true
        }

        try {
          const res = await fetch("/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username: email, password }),
          })

          if (!res.ok) return false

          const data = await res.json()
          if (!data.ok || !data.user) return false

          set({
            user: {
              ...data.user,
              isPro: !!data.user.isPro,
              isBasic: !!data.user.isBasic,
              isPremium: !!data.user.isPremium,
            },
            isAuthenticated: true,
            isDemo: false,
          })

          return true
        } catch (err) {
          console.error("Login error:", err)
          return false
        }
      },

      logout: () => {
        set({ user: null, isAuthenticated: false, isDemo: false })
      },

      checkRole: (allowedRoles: UserRole[]) => {
        const { user } = get()
        if (!user) return false
        return allowedRoles.includes(user.role)
      },

      refreshUserStatus: async () => {
        const { user, isDemo } = get()
        if (!user || isDemo) return

        try {
          const res = await fetch(`/cliente/pro-status?clientId=${user.name}`)
          if (!res.ok) return
          const data = await res.json()
          
          if (data.ok) {
            set((state) => ({
              user: state.user ? {
                ...state.user,
                isPro: !!data.isPro,
                isBasic: !!data.isBasic,
                isPremium: !!data.isPremium,
                role: data.userRole as UserRole
              } : null
            }))
          }
        } catch (err) {
          console.error("Refresh status error:", err)
        }
      },
    }),
    {
      name: "auth-storage",
    },
  ),
)

/* =========================
   DATA STORE (SENSORES)
========================= */

interface DataStore {
  measurements: Measurement[]
  alerts: Alert[]
  totalAlerts: number
  sensors: SensorInfo[]
  alarmLimits: AlarmLimits
  lastUpdated: Date | null
  isConnected: boolean
  gatewayConnected: boolean
  setMeasurements: (data: Measurement[]) => void
  setAlerts: (alerts: Alert[]) => void
  setAlarmLimits: (limits: AlarmLimits) => void
  setConnected: (connected: boolean) => void
  setGatewayConnected: (connected: boolean) => void
  updateSensorStatus: () => void
}

const DEFAULT_ALARM_LIMITS: AlarmLimits = {
  tempAmbiente: { min: 0, max: 25 },
  humedad: { min: 0, max: 100 },
  vibracion: { min: 0, max: 1.0 },
  tempUltra: { min: 0, max: 80 },
  ultra: { min: 0, max: 80 },
  rms: { min: 0, max: 80 },
  pico: { min: 0, max: 500 },
  acc_total: { min: 0, max: 16 },
  co_level: { min: 0, max: 35 },
  fireTemp: 45,
}

export const useDataStore = create<DataStore>()((set, get) => ({
  measurements: [],
  alerts: [],
  totalAlerts: 0,
  sensors: [],
  alarmLimits: DEFAULT_ALARM_LIMITS,
  lastUpdated: null,
  isConnected: false,
  gatewayConnected: false,

  setMeasurements: (data) => {
    set({ measurements: data, lastUpdated: new Date() })
    get().updateSensorStatus()
  },

  setAlerts: (alerts) => {
    set({ alerts, totalAlerts: alerts.length })
    get().updateSensorStatus()
  },

  setAlarmLimits: (limits) => {
    set({ alarmLimits: { ...DEFAULT_ALARM_LIMITS, ...limits } })
    get().updateSensorStatus()
  },

  setConnected: (connected) => set({ isConnected: connected }),

  setGatewayConnected: (connected) => set({ gatewayConnected: connected }),

  updateSensorStatus: () => {
    const { measurements, alarmLimits } = get()
    const sensorMap = new Map<string, SensorInfo>()

    const evalLevel = (
      value: number | null | undefined,
      limits: { min?: number; max: number },
    ) => {
      if (value == null || Number.isNaN(value)) return "ok"
      const { min, max } = limits
      if (min != null) {
        if (value < min * 0.9) return "error"
        if (value < min) return "warning"
      }
      if (value > max * 1.1) return "error"
      if (value > max) return "warning"
      return "ok"
    }

    measurements.forEach((m) => {
      if (!m.sensor_id) return
      if (sensorMap.has(m.sensor_id)) return

      const tipo = (m.tipo_sensor || "").toLowerCase()
      if (tipo !== "tmp" && tipo !== "mchn" && tipo !== "all") return

      const levels: ("ok" | "warning" | "error")[] = []

      if (tipo === "tmp") {
        levels.push(evalLevel(m.temperatura, alarmLimits.tempAmbiente))
      } else if (tipo === "all") {
        levels.push(evalLevel(m.temperatura, alarmLimits.tempAmbiente))
        levels.push(evalLevel(m.temperatura_ultrasound, alarmLimits.tempUltra))
        levels.push(evalLevel(m.vibracion, alarmLimits.vibracion))
        levels.push(evalLevel(m.ultrasound, alarmLimits.ultra))
        levels.push(evalLevel(m.rms, alarmLimits.rms))
        levels.push(evalLevel(m.pico, alarmLimits.pico))
        levels.push(evalLevel(m.acc_total, alarmLimits.acc_total))
        levels.push(evalLevel(m.co_level, alarmLimits.co_level))
      } else {
        levels.push(evalLevel(m.temperatura_ultrasound, alarmLimits.tempUltra))
        levels.push(evalLevel(m.vibracion, alarmLimits.vibracion))
        levels.push(evalLevel(m.ultrasound, alarmLimits.ultra))
        levels.push(evalLevel(m.rms, alarmLimits.rms))
        levels.push(evalLevel(m.pico, alarmLimits.pico))
        levels.push(evalLevel(m.acc_total, alarmLimits.acc_total))
      }

      const status = levels.includes("error")
        ? "error"
        : levels.includes("warning")
        ? "warning"
        : "online"

      sensorMap.set(m.sensor_id, {
        id: m.sensor_id,
        name: m.dispositivo_nombre || m.sensor_id,
        location: m.dispositivo_ubicacion || "Desconocida",
        status,
        lastReading: m,
        tipo_sensor: tipo,
      })
    })

    set({ sensors: Array.from(sensorMap.values()) })
  },
}))

/* =========================
   UI STORE
========================= */

interface UIStore {
  sidebarOpen: boolean
  currentModule: string
  toggleSidebar: () => void
  setCurrentModule: (module: string) => void
}

export const useUIStore = create<UIStore>()((set) => ({
  sidebarOpen: true,
  currentModule: "dashboard",
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setCurrentModule: (module) => set({ currentModule: module }),
}))

/* =========================
   SITE BUILDER
========================= */

interface SiteBuilderStore {
  blocks: SiteBlock[]
  floorSize: [number, number]
  sensorPositions: Record<string, [number, number, number]>
  setBlocks: (updater: (prev: SiteBlock[]) => SiteBlock[]) => void
  setFloorSize: (size: [number, number]) => void
  setSensorPosition: (id: string, pos: [number, number, number]) => void
  resetBlocks: () => void
  fetchMapConfig: () => Promise<void>
  saveMapConfig: (username?: string) => Promise<boolean>
}

export const useSiteBuilderStore = create<SiteBuilderStore>()(
  persist(
    (set, get) => ({
      blocks: [],
      floorSize: [120, 120],
      sensorPositions: {},

      setBlocks: (updater) =>
        set((state) => ({ blocks: updater(state.blocks) })),

      setFloorSize: (size) => set({ floorSize: size }),

      setSensorPosition: (id, position) =>
        set((state) => ({
          sensorPositions: { ...state.sensorPositions, [id]: position },
        })),

      resetBlocks: () => set({ blocks: [] }),

      fetchMapConfig: async () => {
        try {
          const res = await fetch("/map/config?name=default")
          if (res.ok) {
            const data = await res.json()
            if (data.ok && data.config) {
              set({
                blocks: data.config.blocks || [],
                floorSize: data.config.floorSize || [120, 120],
                sensorPositions: data.config.sensorPositions || {},
              })
            }
          }
        } catch (err) {
          console.error("Error fetching map config:", err)
        }
      },

      saveMapConfig: async (username) => {
        try {
          const { blocks, floorSize, sensorPositions } = get()
          const res = await fetch("/map/config", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: "default",
              blocks,
              floorSize,
              sensorPositions,
              username,
            }),
          })
          return res.ok
        } catch (err) {
          console.error("Error saving map config:", err)
          return false
        }
      },
    }),
    {
      name: "site-builder-blocks",
      version: 4,
      migrate: (state: any, version: number) => {
        const base = state || {}
        return {
          blocks: Array.isArray(base.blocks) ? base.blocks : [],
          floorSize: Array.isArray(base.floorSize) ? base.floorSize : [120, 120],
          sensorPositions:
            base.sensorPositions && typeof base.sensorPositions === "object"
              ? base.sensorPositions
              : {},
        }
      },
    },
  ),
)

/* =========================
   INCIDENT NAVIGATION
========================= */

interface IncidentNavigationSource {
  blockId: string
  blockLabel: string
  sensorIds: string[]
  metric: string
  timestamp: string
}

interface IncidentNavigationStore {
  source: IncidentNavigationSource | null
  setIncidentSource: (source: IncidentNavigationSource | null) => void
  clearIncidentSource: () => void
}

export const useIncidentNavigationStore =
  create<IncidentNavigationStore>()((set) => ({
    source: null,
    setIncidentSource: (source) => set({ source }),
    clearIncidentSource: () => set({ source: null }),
  }))
